import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { type SchedulerFollowUpDistributionBucket } from '@libraries/typescript/api_client';

type SchedulerFollowUpDistributionChartProps = {
    readonly buckets: Array<SchedulerFollowUpDistributionBucket>;
    readonly onBucketClick?: (point: DistributionPoint) => void;
};

type DistributionPoint = {
    readonly label: string;
    readonly followUpCountMin: number;
    readonly followUpCountMax: number | null;
    readonly filled: number;
    readonly contactedNoYes: number;
    readonly noCaregiversSuggested: number;
    readonly shiftCancelled: number;
    readonly otherNonFill: number;
    readonly inFlight: number;
    readonly staleIdle: number;
};

type DistributionSeriesKey = Exclude<
    keyof DistributionPoint,
    'label' | 'followUpCountMin' | 'followUpCountMax'
>;

const SERIES: Array<{
    readonly key: DistributionSeriesKey;
    readonly label: string;
    readonly color: string;
}> = [
    { key: 'filled', label: 'Filled callouts', color: 'var(--chart-2)' },
    {
        key: 'contactedNoYes',
        label: 'Contacted, no yes',
        color: 'var(--chart-1)',
    },
    {
        key: 'noCaregiversSuggested',
        label: 'No caregivers suggested',
        color: 'var(--destructive)',
    },
    {
        key: 'shiftCancelled',
        label: 'Shift cancelled',
        color: 'var(--chart-4)',
    },
    { key: 'otherNonFill', label: 'Other non-fill', color: 'var(--chart-5)' },
    { key: 'inFlight', label: 'In flight', color: 'var(--muted-foreground)' },
    { key: 'staleIdle', label: 'Stale / abandoned', color: '#991b1b' },
];

function toDistributionPoint(
    bucket: SchedulerFollowUpDistributionBucket,
): DistributionPoint {
    return {
        label: bucket.label,
        followUpCountMin: bucket.follow_up_count_min,
        followUpCountMax: bucket.follow_up_count_max,
        filled: bucket.filled_count,
        contactedNoYes: bucket.contacted_no_yes_count,
        noCaregiversSuggested: bucket.no_caregivers_suggested_count,
        shiftCancelled: bucket.shift_cancelled_count,
        otherNonFill: bucket.other_non_fill_count,
        inFlight: bucket.in_flight_count,
        staleIdle: bucket.stale_idle_count,
    };
}

type DistributionTooltipEntry = {
    readonly name?: string;
    readonly value?: number;
    readonly color?: string;
};

function DistributionTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<DistributionTooltipEntry>;
    readonly label?: string;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
    const followUpLabel = label === '1' ? 'follow-up' : 'follow-ups';
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">
                {label} scheduler {followUpLabel}
            </div>
            {payload.map((entry) => (
                <div
                    key={entry.name}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">{entry.value}</span>
                </div>
            ))}
            <div className="text-muted-foreground mt-1 border-t pt-1">
                Total: {total}
            </div>
        </div>
    );
}

export function SchedulerFollowUpDistributionChart({
    buckets,
    onBucketClick,
}: SchedulerFollowUpDistributionChartProps) {
    const data = buckets.map(toDistributionPoint);
    const total = buckets.reduce((sum, bucket) => sum + bucket.num_runs, 0);
    if (data.length === 0 || total === 0) {
        return (
            <div className="text-muted-foreground flex h-72 items-center justify-center rounded-md border text-sm">
                No data to plot.
            </div>
        );
    }

    return (
        <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    margin={{ left: 8, right: 8 }}
                    onClick={
                        onBucketClick
                            ? (state: { readonly activeLabel?: unknown }) => {
                                  const point = data.find(
                                      (row) => row.label === state.activeLabel,
                                  );
                                  if (point !== undefined) {
                                      onBucketClick(point);
                                  }
                              }
                            : undefined
                    }
                    className={onBucketClick ? 'cursor-pointer' : undefined}
                >
                    <CartesianGrid
                        stroke="var(--border)"
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip content={<DistributionTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {SERIES.map((series) => (
                        <Bar
                            key={series.key}
                            dataKey={series.key}
                            name={series.label}
                            stackId="outcomes"
                            fill={series.color}
                            isAnimationActive={false}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
